import React, { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useProjects } from '../../hooks/useProjects';
import { useCustomers } from '../../hooks/useCustomers';
import { useUsers } from '../../hooks/useUsers';
import Modal from '../common/Modal';
import SearchableSelect from '../common/SearchableSelect';
import { 
  BoltIcon, 
  DocumentPlusIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

interface BulkTaskCreatorProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateTasks: (tasks: BulkTaskData[]) => Promise<void>;
}

export interface BulkTaskData {
  title: string;
  description?: string;
  dueDate?: Date;
  estimatedHours?: number;
  actualHours?: number;
  projectId?: string;
  customerId?: string;
  assigneeId?: string;
  assigneeName?: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'todo' | 'in_progress' | 'completed' | 'cancelled';
  /** behúzott sorokból (szóköz/tab/„-”) képzett alfeladatok */
  subtasks?: string[];
}

const BulkTaskCreator: React.FC<BulkTaskCreatorProps> = ({
  isOpen,
  onClose,
  onCreateTasks
}) => {
  const { t } = useLanguage();
  const { projects } = useProjects();
  const { customers } = useCustomers();
  const { users } = useUsers();

  const [taskText, setTaskText] = useState('');
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [selectedCustomer, setSelectedCustomer] = useState<string>('');
  const [selectedAssignee, setSelectedAssignee] = useState<string>('');
  const [defaultPriority, setDefaultPriority] = useState<'low' | 'medium' | 'high' | 'urgent'>('medium');
  const [defaultStatus, setDefaultStatus] = useState<'todo' | 'in_progress' | 'completed' | 'cancelled'>('todo');
  const [isCreating, setIsCreating] = useState(false);

  // Sorok: a behúzott (szóköz/tab) vagy „- ” kezdetű sor az előző feladat alfeladata
  const isSubLine = (line: string) => /^\s+\S/.test(line) || /^\s*[-*•]\s+/.test(line);
  const blocks = React.useMemo(() => {
    const out: { line: string; subtasks: string[] }[] = [];
    taskText.split('\n').forEach(raw => {
      if (raw.trim() === '') return;
      if (isSubLine(raw) && out.length) { out[out.length - 1].subtasks.push(raw.replace(/^\s*[-*•]?\s*/, '').trim()); }
      else out.push({ line: raw.trim(), subtasks: [] });
    });
    return out;
  }, [taskText]);
  const taskLines = blocks.map(b => b.line);
  const taskCount = taskLines.length;
  const subtaskCount = blocks.reduce((n, b) => n + b.subtasks.length, 0);

  const parseTaskLine = (line: string) => {
    let title = line.trim();
    let description: string | undefined = undefined;
    let dueDate: Date | undefined = undefined;
    let estimatedHours: number | undefined = undefined;
    let actualHours: number | undefined = undefined;
    let assigneeId = selectedAssignee;
    let assigneeName = selectedAssignee ? users.find(u => u.id === selectedAssignee)?.displayName : undefined;
    let priority = defaultPriority;

    let invalid: string | undefined;
    // Ha van pipe (|) szeparátor, akkor oszlopokra bontjuk
    // Formátum: határidő | leírás | felelős@email | perc
    if (line.includes('|')) {
      const parts = line.split('|').map(p => p.trim());
      if (!parts[1]) invalid = 'Hiányzik a cím (2. oszlop)';
      if (parts[0] && !parseDateString(parts[0])) invalid = invalid || `Érvénytelen dátum: „${parts[0]}”`;
      
      // 1. oszlop: határidő (opcionális)
      if (parts[0]) {
        const dueDateStr = parts[0].trim();
        if (dueDateStr) {
          // Próbáljuk meg parse-olni a dátumot (YYYY-MM-DD vagy YYYY.MM.DD vagy MM.DD vagy MM/DD)
          const parsedDate = parseDateString(dueDateStr);
          if (parsedDate) {
            dueDate = parsedDate;
          }
        }
      }
      
      // 2. oszlop: leírás (kötelező, ez lesz a title)
      if (parts[1]) {
        title = parts[1].trim();
      }
      
      // 3. oszlop: felelős email (opcionális)
      if (parts[2]) {
        const emailStr = parts[2].trim();
        if (emailStr) {
          const foundUser = users.find(u => 
            u.email?.toLowerCase() === emailStr.toLowerCase() ||
            u.displayName?.toLowerCase().includes(emailStr.toLowerCase())
          );
          
          if (foundUser) {
            assigneeId = foundUser.id;
            assigneeName = foundUser.displayName;
          }
        }
      }
      
      // 4. oszlop: becsült idő (perc; „90p”, „1.5h”, „1,5ó”, „2:30” is)
      if (parts[3]) estimatedHours = parseDurationHours(parts[3]);

      // 5. oszlop: ráfordított idő
      if (parts[4]) actualHours = parseDurationHours(parts[4]);
    }
    {
      // Címkék a címben (mindkét formátumnál): @felelős és #prioritás a sor bármely részén
      // magyar és angol címkék: #alacsony/#low, #közepes/#medium, #magas/#high, #sürgős/#urgent
      const PRIO: Record<string, 'low' | 'medium' | 'high' | 'urgent'> = { low: 'low', alacsony: 'low', medium: 'medium', közepes: 'medium', kozepes: 'medium', high: 'high', magas: 'high', urgent: 'urgent', sürgős: 'urgent', surgos: 'urgent' };
      const priorityMatch = title.match(/(?:^|\s)#(low|medium|high|urgent|alacsony|közepes|kozepes|magas|sürgős|surgos)\b/i);
      if (priorityMatch) {
        priority = PRIO[priorityMatch[1].toLowerCase()] || priority;
        title = title.replace(priorityMatch[0], ' ').replace(/\s{2,}/g, ' ').trim();
      }

      // Parse assignee (@email or @name) – a sor bármely részén
      const assigneeMatch = title.match(/(?:^|\s)@([^\s#]+)/);
      if (assigneeMatch) {
        const identifier = assigneeMatch[1];
        // Keresés email vagy név alapján
        const foundUser = users.find(u => 
          u.email?.toLowerCase() === identifier.toLowerCase() ||
          u.displayName?.toLowerCase().includes(identifier.toLowerCase())
        );
        
        if (foundUser) {
          assigneeId = foundUser.id;
          assigneeName = foundUser.displayName;
        }
        
        title = title.replace(assigneeMatch[0], ' ').replace(/\s{2,}/g, ' ').trim();
      }
    }

    if (!invalid && !title.trim()) invalid = 'Üres cím';
    return {
      invalid,
      title,
      description,
      dueDate,
      estimatedHours,
      actualHours,
      assigneeId,
      assigneeName,
      priority
    };
  };

  // Időtartam → óra. Alapértelmezés: perc. „90p”, „1.5h”, „1,5ó”, „2:30” elfogadott.
  const parseDurationHours = (raw: string): number | undefined => {
    const v = raw.trim().toLowerCase().replace(',', '.');
    if (!v) return undefined;
    const hm = v.match(/^(\d{1,3}):(\d{1,2})$/);
    if (hm) return parseInt(hm[1]) + parseInt(hm[2]) / 60;
    const m = v.match(/^(\d+(?:\.\d+)?)\s*(h|ó|óra|p|perc|m|min)?$/);
    if (!m) return undefined;
    const n = parseFloat(m[1]);
    if (!(n > 0)) return undefined;
    const unit = m[2] || '';
    const hours = unit === 'h' || unit === 'ó' || unit === 'óra' ? n : n / 60;
    return Math.round(hours * 100) / 100;
  };

  // Dátum parse segédfüggvény
  const parseDateString = (dateStr: string): Date | undefined => {
    if (!dateStr) return undefined;
    
    const today = new Date();
    const currentYear = today.getFullYear();
    
    const str = dateStr.trim().replace(/\.$/, ''); // „2026.10.15.” záró pont
    const valid = (y: number, m: number, d: number) => { const dt = new Date(y, m - 1, d, 17, 0, 0, 0); return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d ? dt : undefined; };

    // YYYY-MM-DD vagy YYYY.MM.DD formátum
    const fullDateMatch = str.match(/^(\d{4})[-./](\d{1,2})[-./](\d{1,2})$/);
    if (fullDateMatch) {
      const [, year, month, day] = fullDateMatch;
      return valid(parseInt(year), parseInt(month), parseInt(day));
    }
    
    // MM-DD vagy MM.DD vagy MM/DD formátum (idei év; ha már 30+ napja elmúlt, jövő év)
    const shortDateMatch = str.match(/^(\d{1,2})[-./](\d{1,2})$/);
    if (shortDateMatch) {
      const [, month, day] = shortDateMatch;
      const d = valid(currentYear, parseInt(month), parseInt(day));
      if (d && d.getTime() < today.getTime() - 30 * 86400000) return valid(currentYear + 1, parseInt(month), parseInt(day));
      return d;
    }
    
    return undefined;
  };

  const handleCreate = async () => {
    if (taskLines.length === 0) {
      toast.error('Adj meg legalább egy feladatot!');
      return;
    }

    setIsCreating(true);
    try {
      const skipped = blocks.filter(b => parseTaskLine(b.line).invalid).length;
      const tasks: BulkTaskData[] = blocks.filter(b => !parseTaskLine(b.line).invalid).map(({ line, subtasks }) => {
        const parsed = parseTaskLine(line);
        return {
          subtasks,
          title: parsed.title,
          description: parsed.description,
          dueDate: parsed.dueDate,
          estimatedHours: parsed.estimatedHours,
          actualHours: parsed.actualHours,
          projectId: selectedProject || undefined,
          customerId: selectedCustomer || undefined,
          assigneeId: parsed.assigneeId || undefined,
          assigneeName: parsed.assigneeName || undefined,
          priority: parsed.priority,
          status: defaultStatus
        };
      });

      if (!tasks.length) { toast.error('Nincs érvényes sor'); setIsCreating(false); return; }
      await onCreateTasks(tasks);
      
      toast.success(`${tasks.length} feladat létrehozva${skipped ? `, ${skipped} hibás sor kihagyva` : ''}`);
      handleClose();
    } catch (error) {
      console.error('Error creating bulk tasks:', error);
      toast.error('Hiba történt a feladatok létrehozásakor');
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    setTaskText('');
    setSelectedProject('');
    setSelectedCustomer('');
    setSelectedAssignee('');
    setDefaultPriority('medium');
    setDefaultStatus('todo');
    onClose();
  };

  const projectOptions = [
    { value: '', label: 'Nincs projekt', icon: '📋' },
    ...projects.map(project => ({
      value: project.id,
      label: project.name,
      icon: '📁'
    }))
  ];

  const customerOptions = [
    { value: '', label: 'Nincs ügyfél', icon: '👤' },
    ...customers.map(customer => ({
      value: customer.id,
      label: customer.companyName || `${customer.firstName || ''} ${customer.lastName || ''}`.trim() || 'Névtelen',
      icon: '🏢'
    }))
  ];

  const assigneeOptions = [
    { value: '', label: 'Nincs felelős', icon: '👤' },
    ...users.map(user => ({
      value: user.id,
      label: user.displayName ? `${user.displayName} (${user.email})` : user.email,
      icon: '👨‍💼'
    }))
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="⚡ Gyors Feladat Létrehozás"
      size="5xl"
      zIndex={50}
    >

      <div className="space-y-6">
        {/* Instrukciók */}
        <details className="crm-card group">
          <summary className="flex items-center gap-2 px-4 py-2.5 cursor-pointer select-none text-[13px] font-semibold text-gray-900 dark:text-gray-50 list-none">
            <ExclamationCircleIcon className="h-4 w-4 text-gray-400" />
            Hogyan írjam?
            <span className="ml-auto text-[11px] font-normal text-gray-400 group-open:hidden">megnyitás</span>
          </summary>
          <div className="px-4 pb-4 text-sm text-gray-700 dark:text-gray-300 space-y-3">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">Alap</div>
              <p>Soronként egy feladat. A cím mellé írható <code className="px-1 rounded bg-gray-100 dark:bg-gray-800">@név</code> vagy <code className="px-1 rounded bg-gray-100 dark:bg-gray-800">@email</code> (felelős) és <code className="px-1 rounded bg-gray-100 dark:bg-gray-800">#alacsony #közepes #magas #sürgős</code> (prioritás). Az üres sorok nem számítanak.</p>
            </div>
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">Alfeladatok</div>
              <p>A feladat alá <b>behúzva</b> (szóköz/tab) vagy <code className="px-1 rounded bg-gray-100 dark:bg-gray-800">- </code> jellel kezdve írt sorok az adott feladat alfeladatai lesznek.</p>
            </div>
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">Részletes sor (| jellel elválasztva)</div>
              <code className="block px-2 py-1.5 rounded bg-gray-100 dark:bg-gray-800 text-xs">határidő | cím | felelős | becsült perc | ráfordított perc</code>
              <ul className="mt-1.5 text-xs text-gray-600 dark:text-gray-400 space-y-0.5">
                <li>Határidő: <code>2026-10-15</code>, <code>2026.10.15</code> vagy <code>10-15</code> (üresen hagyható)</li>
                <li>Cím: kötelező (benne is lehet <code>@felelős</code>, <code>#prioritás</code>) · Felelős: név vagy e-mail (üresen hagyható) · Idő: perc, vagy <code>90p</code>, <code>1.5h</code>, <code>2:30</code>; a ráfordított idő időbejegyzésként rögzül a te nevedre</li>
              </ul>
            </div>
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">Példa</div>
              <pre className="px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-800 text-xs leading-5 overflow-x-auto">{`Klíma telepítés – Kovács Kft. @peter #magas
  - Anyag kiszedése raktárból
  - Beltéri egység felszerelése
  - Nyomáspróba, átadás
10-20 | Éves karbantartás – Nagy Bt. | anna@ceg.hu | 120 | 90
Ajánlat kiküldése @anna`}</pre>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">A lent kiválasztott projekt, ügyfél, felelős és prioritás minden sorra érvényes, ha a sor nem ír mást.</p>
          </div>
        </details>

        {/* Projekt, Ügyfél és Felelős választó */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Projekt (opcionális)
            </label>
            <SearchableSelect
              options={projectOptions}
              value={selectedProject}
              onChange={setSelectedProject}
              placeholder="Válassz projektet..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Ügyfél (opcionális)
            </label>
            <SearchableSelect
              options={customerOptions}
              value={selectedCustomer}
              onChange={setSelectedCustomer}
              placeholder="Válassz ügyfelet..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Felelős (opcionális)
            </label>
            <SearchableSelect
              options={assigneeOptions}
              value={selectedAssignee}
              onChange={setSelectedAssignee}
              placeholder="Válassz felelőst..."
            />
          </div>
        </div>

        {/* Alapértelmezett beállítások */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Alapértelmezett prioritás
            </label>
            <select
              value={defaultPriority}
              onChange={(e) => setDefaultPriority(e.target.value as any)}
              className="w-full px-4 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:border-blue-500 dark:focus:border-blue-400 focus:ring-4 focus:ring-blue-500/20 transition-all"
            >
              <option value="low">🟢 Alacsony</option>
              <option value="medium">🟡 Közepes</option>
              <option value="high">🟠 Magas</option>
              <option value="urgent">🔴 Sürgős</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Alapértelmezett státusz
            </label>
            <select
              value={defaultStatus}
              onChange={(e) => setDefaultStatus(e.target.value as any)}
              className="w-full px-4 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:border-blue-500 dark:focus:border-blue-400 focus:ring-4 focus:ring-blue-500/20 transition-all"
            >
              <option value="todo">📋 Tennivaló</option>
              <option value="in_progress">🔄 Folyamatban</option>
              <option value="completed">✅ Kész</option>
              <option value="cancelled">❌ Törölve</option>
            </select>
          </div>
        </div>

        {/* Feladat szöveg beviteli mező */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Feladatok (soronként egy)
            </label>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {taskCount} feladat{subtaskCount ? ` · ${subtaskCount} alfeladat` : ''}
            </span>
          </div>
          <textarea
            value={taskText}
            onChange={(e) => setTaskText(e.target.value)}
            placeholder="Egyszerű formátum:&#10;Weboldal design elkészítése @john@example.com #high&#10;  - Wireframe&#10;  - Végleges design&#10;Adatbázis migrálás @jane #medium&#10;&#10;Behúzott vagy „- ” kezdetű sor = az előző feladat alfeladata.&#10;&#10;Részletes formátum (pipe szeparált):&#10;2024-12-31 | API fejlesztés | dev@company.com | 120 | 150&#10;12-25 | Karácsonyi kampány | marketing@company.com | 60 | 75&#10;01-15 | Tesztelés | | 90 | 105"
            rows={12}
            className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:border-blue-500 dark:focus:border-blue-400 focus:ring-4 focus:ring-blue-500/20 dark:focus:ring-blue-400/20 transition-all font-mono text-sm"
            disabled={isCreating}
          />
        </div>

        {/* Előnézet – minden sor, minden felismert mező */}
        {taskCount > 0 && (() => {
          const rows = blocks.map(b => ({ ...b, parsed: parseTaskLine(b.line) }));
          const bad = rows.filter(r => r.parsed.invalid).length;
          const est = rows.reduce((n, r) => n + (r.parsed.invalid ? 0 : (r.parsed.estimatedHours || 0)), 0);
          const act = rows.reduce((n, r) => n + (r.parsed.invalid ? 0 : (r.parsed.actualHours || 0)), 0);
          const PRI: Record<string, { l: string; c: string }> = { low: { l: 'Alacsony', c: 'text-emerald-600' }, medium: { l: 'Közepes', c: 'text-amber-600' }, high: { l: 'Magas', c: 'text-orange-600' }, urgent: { l: 'Sürgős', c: 'text-rose-600' } };
          const fmtH = (h?: number) => (h ? (h < 1 ? `${Math.round(h * 60)}p` : `${Math.round(h * 10) / 10}h`) : '');
          const projectName = projects.find(p => p.id === selectedProject)?.name;
          const customerName = customers.find(c => c.id === selectedCustomer)?.companyName || customers.find(c => c.id === selectedCustomer)?.firstName;
          return (
            <div className="crm-card overflow-hidden">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 border-b border-[var(--crm-border)] text-sm">
                <span className="font-semibold text-gray-900 dark:text-gray-50">Előnézet</span>
                <span className="text-gray-500 dark:text-gray-400">{taskCount - bad} feladat{subtaskCount ? ` · ${subtaskCount} alfeladat` : ''}{est ? ` · becsült ${fmtH(est)}` : ''}{act ? ` · ráfordított ${fmtH(act)}` : ''}</span>
                {bad > 0 && <span className="text-rose-600 dark:text-rose-400">· {bad} hibás sor kimarad</span>}
                {(projectName || customerName) && <span className="ml-auto text-xs text-gray-500 dark:text-gray-400">{[projectName, customerName].filter(Boolean).join(' · ')}</span>}
              </div>
              <div className="overflow-x-auto max-h-80 overflow-y-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr>
                      <th className="px-3 text-left w-8">#</th>
                      <th className="px-3 text-left">Cím</th>
                      <th className="px-3 text-left whitespace-nowrap">Határidő</th>
                      <th className="px-3 text-left">Felelős</th>
                      <th className="px-3 text-left">Prioritás</th>
                      <th className="px-3 text-right whitespace-nowrap">Becsült</th>
                      <th className="px-3 text-right whitespace-nowrap">Ráford.</th>
                      <th className="px-3 text-right whitespace-nowrap">Alfeladat</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, idx) => (
                      <React.Fragment key={idx}>
                        <tr className={r.parsed.invalid ? 'bg-rose-50/60 dark:bg-rose-900/10' : ''}>
                          <td className="px-3 text-gray-400 tabular-nums">{idx + 1}</td>
                          <td className="px-3">
                            <div className="text-gray-900 dark:text-gray-100">{r.parsed.title || <span className="text-gray-400 italic">(nincs cím)</span>}</div>
                            {r.parsed.invalid && <div className="text-[11px] text-rose-600 dark:text-rose-400">⚠ {r.parsed.invalid}</div>}
                          </td>
                          <td className="px-3 whitespace-nowrap text-gray-700 dark:text-gray-300">{r.parsed.dueDate ? r.parsed.dueDate.toLocaleDateString('hu-HU') : <span className="text-gray-300 dark:text-gray-600">–</span>}</td>
                          <td className="px-3 whitespace-nowrap text-gray-700 dark:text-gray-300">{r.parsed.assigneeName || <span className="text-gray-300 dark:text-gray-600">–</span>}</td>
                          <td className={`px-3 whitespace-nowrap ${PRI[r.parsed.priority]?.c || ''}`}>{PRI[r.parsed.priority]?.l || r.parsed.priority}</td>
                          <td className="px-3 text-right tabular-nums text-gray-700 dark:text-gray-300">{fmtH(r.parsed.estimatedHours)}</td>
                          <td className="px-3 text-right tabular-nums text-gray-700 dark:text-gray-300">{fmtH(r.parsed.actualHours)}</td>
                          <td className="px-3 text-right tabular-nums text-gray-700 dark:text-gray-300">{r.subtasks.length || ''}</td>
                        </tr>
                        {r.subtasks.map((st, k) => (
                          <tr key={`${idx}-${k}`} className="text-xs">
                            <td className="px-3" />
                            <td className="px-3 pl-7 text-gray-600 dark:text-gray-400" colSpan={7}><span className="inline-block w-3 h-3 mr-1.5 align-[-2px] rounded border border-gray-300 dark:border-gray-600" />{st}</td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end space-x-3 pt-6 mt-6 border-t-2 border-gray-100 dark:border-gray-700">
        <button
          onClick={handleClose}
          disabled={isCreating}
          className="px-5 py-2.5 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl font-medium transition-colors disabled:opacity-50"
        >
          Mégse
        </button>
        <button
          onClick={handleCreate}
          disabled={isCreating || taskCount === 0}
          className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          <DocumentPlusIcon className="h-5 w-5" />
          <span>
            {isCreating ? 'Létrehozás...' : `${taskCount} Feladat Létrehozása`}
          </span>
        </button>
      </div>
    </Modal>
  );
};

export default BulkTaskCreator;
